import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import css from './FieldPhoneNumberInput.module.css';

/**
 * Custom country select for react-phone-number-input with a scrollable panel.
 * Native <select> cannot honor max-height on its dropdown list.
 *
 * @component
 */
const CountrySelectDropdown = props => {
  const {
    value,
    onChange,
    options = [],
    disabled,
    readOnly,
    iconComponent: Icon,
    arrowComponent: Arrow,
    maxHeight = 240,
    className,
    'aria-label': ariaLabel,
    name,
    onFocus,
    onBlur,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const selectableOptions = useMemo(
    () => options.filter(option => !option.divider),
    [options]
  );

  const selectedOption = useMemo(
    () => selectableOptions.find(option => option.value === value) || selectableOptions[0],
    [selectableOptions, value]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDown = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled || readOnly) {
      return;
    }
    setIsOpen(open => !open);
  };

  const handleSelect = nextValue => {
    onChange(nextValue === 'ZZ' ? undefined : nextValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={classNames('PhoneInputCountry', css.countrySelectRoot, className)}
    >
      <button
        type="button"
        name={name}
        className={classNames('PhoneInputCountrySelect', css.countrySelectTrigger)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled || readOnly}
        onClick={handleToggle}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {selectedOption && Icon ? (
        <Icon aria-hidden country={value} label={selectedOption.label} />
      ) : null}
      {Arrow ? <Arrow /> : <div className="PhoneInputCountrySelectArrow" />}

      {isOpen ? (
        <ul
          className={css.countrySelectDropdown}
          role="listbox"
          style={{ maxHeight }}
        >
          {selectableOptions.map(option => {
            const optionValue = option.value || 'ZZ';
            const isSelected = option.value === value;
            return (
              <li key={optionValue} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={classNames(css.countrySelectOption, {
                    [css.countrySelectOptionActive]: isSelected,
                  })}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default CountrySelectDropdown;
